import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { NavBar } from './NavBar';
import {
  BarChart3, TrendingUp, ArrowRightLeft, Award, Gauge,
  CheckCircle, AlertTriangle, Clock, RefreshCw
} from 'lucide-react';

interface MassBalanceKPI {
  balanced:    number;
  minor:       number;
  check:       number;
  total:       number;
}

interface SummaryKPI {
  inbound_tons:  string;
  outbound_tons: string;
  variance_tons: string;
  period_label:  string;
}

interface ActivityItem {
  title:       string;
  description: string;
  timestamp:   string;
  status:      'success' | 'warning' | 'error';
}

// ─── Quick Action Card ────────────────────────────────────────────────────────

interface QuickActionCardProps {
  title:       string;
  description: string;
  icon:        React.ReactNode;
  onClick:     () => void;
  color:       string;
}

function QuickActionCard({ title, description, icon, onClick, color }: QuickActionCardProps) {
  return (
    <button
      onClick={onClick}
      className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-all border border-gray-200 text-left group hover:-translate-y-0.5"
    >
      <div className={`inline-flex items-center justify-center w-12 h-12 ${color} text-white rounded-xl mb-4 group-hover:scale-110 transition-transform`}>
        {icon}
      </div>
      <h4 className="text-base font-semibold text-gray-900 mb-1">{title}</h4>
      <p className="text-sm text-gray-500">{description}</p>
    </button>
  );
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────

function KpiCard({ label, value, sub, icon, color, bgColor }: {
  label:   string;
  value:   string;
  sub?:    string;
  icon:    React.ReactNode;
  color:   string;
  bgColor: string;
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
      <div className="flex items-start justify-between mb-3">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</p>
        <div className={`${bgColor} ${color} p-2 rounded-lg`}>{icon}</div>
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
    </div>
  );
}

// ─── Dashboard ────────────────────────────────────────────────────────────────

export function Dashboard() {
  const { user, getToken } = useAuth();
  const navigate           = useNavigate();

  const now = new Date();
  // Default the dashboard to the PREVIOUS month, not the current one — most
  // companies upload and process their data after month-end, so the current
  // month is typically still empty when someone logs in mid-month.
  const nowYear  = now.getFullYear();
  const nowMonth = now.getMonth() + 1; // 1–12
  const currentYear  = nowMonth === 1 ? nowYear - 1 : nowYear;
  const currentMonth = nowMonth === 1 ? 12 : nowMonth - 1;
  const monthName = new Date(currentYear, currentMonth - 1, 1).toLocaleString('default', { month: 'long' });

  const [kpiLoading,  setKpiLoading]  = useState(true);
  const [massBalance, setMassBalance] = useState<MassBalanceKPI>({ balanced: 0, minor: 0, check: 0, total: 0 });
  const [summary,     setSummary]     = useState<SummaryKPI | null>(null);

  const API_URL = process.env.REACT_APP_API_URL ?? '';

  // Load KPI data on mount
  useEffect(() => {
    const load = async () => {
      try {
        const token   = await getToken();
        const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

        // Load mass balance and inbound in parallel
        const [mbRes, ibRes] = await Promise.all([
          fetch(`${API_URL}/reports/generate`, {
            method: 'POST', headers,
            body: JSON.stringify({
              report_id: '2.1',
              params: { year: String(currentYear), month: String(currentMonth) },
            }),
          }),
          fetch(`${API_URL}/reports/generate`, {
            method: 'POST', headers,
            body: JSON.stringify({
              report_id: 'dashboard.inbound',
              params: { year: String(currentYear), month: String(currentMonth) },
            }),
          }),
        ]);

        const mbData = await mbRes.json();
        const ibData = await ibRes.json();

        // Compute mass balance KPIs
        if (mbData.data?.length) {
          const rows = mbData.data;
          setMassBalance({
            balanced: rows.filter((r: any) => r.status === 'Balanced').length,
            minor:    rows.filter((r: any) => r.status === 'Minor (<5%)').length,
            check:    rows.filter((r: any) => r.status === 'Check').length,
            total:    rows.length,
          });
        }

        // Compute inbound/outbound totals
        if (ibData.data?.length) {
          const totalInbound = ibData.data.reduce((s: number, r: any) => s + parseFloat(r.total_tons || '0'), 0);
          setSummary({
            inbound_tons:  totalInbound.toLocaleString('en-US', { maximumFractionDigits: 1 }),
            outbound_tons: '—',
            variance_tons: '—',
            period_label:  `${monthName} ${currentYear}`,
          });
        }
      } catch (e) {
        console.error('KPI load error:', e);
      } finally {
        setKpiLoading(false);
      }
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const quickActions: QuickActionCardProps[] = [
    {
      title:       'Mass Balance',
      description: 'Material flow reconciliation by facility',
      icon:        <BarChart3 className="w-6 h-6" />,
      onClick:     () => navigate('/reports/mass-balance'),
      color:       'bg-blue-600',
    },
    {
      title:       'Regional Tonnage',
      description: 'Company-wide tonnage trend by material group',
      icon:        <TrendingUp className="w-6 h-6" />,
      onClick:     () => navigate('/reports/regional-tonnage'),
      color:       'bg-emerald-600',
    },
    {
      title:       'Facility-to-Facility Flow',
      description: 'Where material moves between facilities',
      icon:        <ArrowRightLeft className="w-6 h-6" />,
      onClick:     () => navigate('/reports/facility-flow'),
      color:       'bg-cyan-600',
    },
    {
      title:       'Facility Performance Scorecard',
      description: 'Per-facility KPI summary for a selected period',
      icon:        <Award className="w-6 h-6" />,
      onClick:     () => navigate('/reports/facility-scorecard'),
      color:       'bg-amber-600',
    },
    {
      title:       'Landfill Capacity Limits',
      description: 'Monthly tonnage limits and what needs attention',
      icon:        <Gauge className="w-6 h-6" />,
      onClick:     () => navigate('/reports/capacity-limits'),
      color:       'bg-teal-600',
    },
  ];

  const recentActivity: ActivityItem[] = [
    {
      title:       'Files Uploaded',
      description: 'acs2-tickets-feb2026.csv processed successfully',
      timestamp:   '2 hours ago',
      status:      'success',
    },
    {
      title:       'Mass Balance Report',
      description: `Generated for ${monthName} ${currentYear}`,
      timestamp:   '1 day ago',
      status:      'success',
    },
    {
      title:       'Recovery Profiles',
      description: 'Updated for ACS-1 facility',
      timestamp:   '2 days ago',
      status:      'success',
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <NavBar />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">

        {/* Welcome */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            Welcome back, {user?.name?.split(' ')[0]}
          </h2>
          <p className="text-gray-500 mt-1 text-sm">
            {user?.['custom:company_name']} · {monthName} {currentYear}
          </p>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {kpiLoading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 animate-pulse">
                <div className="h-3 bg-gray-200 rounded w-24 mb-4" />
                <div className="h-7 bg-gray-200 rounded w-16" />
              </div>
            ))
          ) : (
            <>
              <KpiCard
                label="Total Inbound"
                value={summary ? summary.inbound_tons + ' tons' : '—'}
                sub={summary?.period_label}
                icon={<TrendingUp className="w-4 h-4" />}
                color="text-green-600"
                bgColor="bg-green-50"
              />
              <KpiCard
                label="Balanced"
                value={String(massBalance.balanced)}
                sub={`of ${massBalance.total} materials`}
                icon={<CheckCircle className="w-4 h-4" />}
                color="text-blue-600"
                bgColor="bg-blue-50"
              />
              <KpiCard
                label="Minor Variance"
                value={String(massBalance.minor)}
                sub="within 5%"
                icon={<AlertTriangle className="w-4 h-4" />}
                color="text-yellow-600"
                bgColor="bg-yellow-50"
              />
              <KpiCard
                label="Needs Review"
                value={String(massBalance.check)}
                sub="mass balance items"
                icon={<AlertTriangle className="w-4 h-4" />}
                color={massBalance.check > 0 ? 'text-red-600' : 'text-gray-600'}
                bgColor={massBalance.check > 0 ? 'bg-red-50' : 'bg-gray-50'}
              />
            </>
          )}
        </div>

        {/* Quick Actions */}
        <div>
          <h3 className="text-base font-bold text-gray-900 mb-4">Management Reports</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {quickActions.map((action, i) => (
              <QuickActionCard key={i} {...action} />
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h3 className="text-base font-bold text-gray-900 mb-4">Recent Activity</h3>
          <div className="space-y-4">
            {recentActivity.map((item, i) => (
              <div key={i} className="flex items-start space-x-4">
                <div className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${
                  item.status === 'success' ? 'bg-green-500' :
                  item.status === 'warning' ? 'bg-yellow-500' : 'bg-red-500'
                }`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-900">{item.title}</p>
                    <span className="text-xs text-gray-400 ml-4 flex-shrink-0">
                      <Clock className="w-3 h-3 inline mr-1" />{item.timestamp}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mt-0.5">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

      </main>
    </div>
  );
}
